import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { moveCardKey } from './dashboardCards';

/**
 * The "Jump back in" grid, with an ARRANGE mode for reordering (2026-10-10).
 *
 * WHY A MODE rather than cards that are always draggable: a card is a link, and
 * on the installed PWA a draggable card cannot also scroll the page or open on a
 * tap reliably (dragging needs `touch-action: none`, which takes the scroll with
 * it). So the cards are plain links until Arrange is on; in the mode they stop
 * navigating, show a grip, and move by drag OR by the arrow buttons. The arrows
 * are always visible in the mode — never hover-only (keratoconus; touch has no
 * hover) — and they are the keyboard path.
 *
 * Drag follows the task board's pointer pattern (BoardView.js): window
 * listeners, a small movement threshold, and a REF carrying the live order,
 * because state read inside the pointerup closure is stale. The grid reorders
 * live as the pointer crosses other cards, so no floating ghost is needed.
 */

const DRAG_THRESHOLD = 6;

const CardBody = ({ card }) => (
	<>
		<span className='accent-bar' />
		<span className='portal-home__card-icon'>
			<i className={`fa-sharp fa-solid ${card.icon}`} />
		</span>
		<h3 className='portal-home__card-title'>{card.label}</h3>
		{card.description && <p className='portal-home__card-text'>{card.description}</p>}
	</>
);

const DashboardCardGrid = ({ cards, arranging, onReorder }) => {
	const keysFromProps = cards.map((c) => c.key).join('|');
	const [order, setOrder] = useState(() => cards.map((c) => c.key));
	const [draggingKey, setDraggingKey] = useState(null);
	const orderRef = useRef(order);
	const dragRef = useRef(null);

	// Follow the saved selection whenever it changes from outside (the picker,
	// another device) — but never mid-drag, or the snapshot would yank the card
	// out from under the pointer.
	useEffect(() => {
		if (dragRef.current) return;
		const next = keysFromProps ? keysFromProps.split('|') : [];
		orderRef.current = next;
		setOrder(next);
	}, [keysFromProps]);

	const byKey = new Map(cards.map((c) => [c.key, c]));

	const commit = (next) => {
		orderRef.current = next;
		setOrder(next);
	};

	const moveBy = (key, delta) => {
		const current = orderRef.current;
		const next = moveCardKey(current, key, current.indexOf(key) + delta);
		if (next !== current) {
			commit(next);
			onReorder(next);
		}
	};

	const onPointerMove = (e) => {
		const d = dragRef.current;
		if (!d) return;
		if (!d.active) {
			if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) < DRAG_THRESHOLD) return;
			d.active = true;
			setDraggingKey(d.key);
		}
		e.preventDefault?.();
		const el = document.elementFromPoint(e.clientX, e.clientY);
		const over = el && el.closest('[data-card-key]');
		const overKey = over && over.getAttribute('data-card-key');
		if (!overKey || overKey === d.key) return;
		const current = orderRef.current;
		const next = moveCardKey(current, d.key, current.indexOf(overKey));
		if (next !== current) commit(next);
	};

	const onPointerUp = () => {
		window.removeEventListener('pointermove', onPointerMove);
		const d = dragRef.current;
		dragRef.current = null;
		setDraggingKey(null);
		if (!d || !d.active) return;
		const final = orderRef.current;
		if (final.join('|') !== d.startOrder.join('|')) onReorder(final);
	};

	const onPointerDown = (e, key) => {
		if (e.button != null && e.button !== 0) return;
		// The arrow buttons are their own controls; a press on one is not a drag.
		if (e.target.closest('button')) return;
		dragRef.current = {
			key,
			startX: e.clientX,
			startY: e.clientY,
			active: false,
			startOrder: orderRef.current,
			// The exact functions registered, so unmount can remove them.
			move: onPointerMove,
			up: onPointerUp,
		};
		window.addEventListener('pointermove', onPointerMove);
		window.addEventListener('pointerup', onPointerUp, { once: true });
	};

	// Release the listeners if the grid unmounts mid-drag.
	useEffect(
		() => () => {
			const d = dragRef.current;
			if (!d) return;
			window.removeEventListener('pointermove', d.move);
			window.removeEventListener('pointerup', d.up);
		},
		[]
	);

	const visible = order.map((k) => byKey.get(k)).filter(Boolean);

	if (!arranging) {
		return (
			<div className='portal-home__grid'>
				{visible.map((card) => (
					<Link
						key={card.key}
						to={card.to}
						className='cyv-card cyv-card--link portal-home__card'
						style={{ '--accent': card.accent }}>
						<CardBody card={card} />
						<span className='portal-home__card-cta'>
							Open <i className='fa-sharp fa-solid fa-arrow-right' />
						</span>
					</Link>
				))}
			</div>
		);
	}

	return (
		<ol className='portal-home__grid portal-home__grid--arranging' aria-label='Dashboard cards, in order'>
			{visible.map((card, i) => (
				<li
					key={card.key}
					data-card-key={card.key}
					className={`cyv-card portal-home__card is-arranging${draggingKey === card.key ? ' is-dragging' : ''}`}
					style={{ '--accent': card.accent }}
					onPointerDown={(e) => onPointerDown(e, card.key)}>
					<span className='portal-home__grip' aria-hidden='true'>
						<i className='fa-sharp fa-solid fa-grip-dots-vertical' />
					</span>
					<CardBody card={card} />
					<span className='portal-home__arrange-row'>
						<button
							type='button'
							className='portal-home__move'
							onClick={() => moveBy(card.key, -1)}
							disabled={i === 0}
							aria-label={`Move ${card.label} earlier`}>
							<i className='fa-sharp fa-solid fa-arrow-left' aria-hidden='true' /> Earlier
						</button>
						<button
							type='button'
							className='portal-home__move'
							onClick={() => moveBy(card.key, 1)}
							disabled={i === visible.length - 1}
							aria-label={`Move ${card.label} later`}>
							Later <i className='fa-sharp fa-solid fa-arrow-right' aria-hidden='true' />
						</button>
					</span>
				</li>
			))}
		</ol>
	);
};

export default DashboardCardGrid;
